import { lazy, Suspense, ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import CookieConsent from "./components/CookieConsent";
import HolidayModeBanner from "./components/HolidayModeBanner";
import AffiliateCookieCatcher from "./components/AffiliateCookieCatcher";
import CanonicalTag from "./components/CanonicalTag";
import MfaVerify from "./components/MfaVerify";
import MobileBottomNav from "./components/MobileBottomNav";
import { usePresence } from "./hooks/usePresence";

// Retry wrapper for lazy imports — handles chunk load failures gracefully
function lazyRetry(factory: () => Promise<{ default: ComponentType<any> }>, retries = 2): ReturnType<typeof lazy> {
  return lazy(() =>
    factory().catch((err) => {
      if (retries > 0) {
        return new Promise<{ default: ComponentType<any> }>((resolve) =>
          setTimeout(() => resolve(lazyRetry(factory, retries - 1) as any), 1000)
        );
      }
      // Final retry failed — force reload to bust stale cache
      window.location.reload();
      return factory();
    })
  );
}

// Lazy-loaded pages with retry
const Index = lazyRetry(() => import("./pages/Index"));
const TutorApplication = lazyRetry(() => import("./pages/TutorApplication"));
const Marketplace = lazyRetry(() => import("./pages/Marketplace"));
const Auth = lazyRetry(() => import("./pages/Auth"));
const Profile = lazyRetry(() => import("./pages/Profile"));
const Messages = lazyRetry(() => import("./pages/Messages"));
const Bookings = lazyRetry(() => import("./pages/Bookings"));
const Earnings = lazyRetry(() => import("./pages/Earnings"));
const TutorProfilePage = lazyRetry(() => import("./pages/TutorProfilePage"));
const AdminDashboard = lazyRetry(() => import("./pages/AdminDashboard"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Classroom = lazyRetry(() => import("./pages/Classroom"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const Portfolio = lazyRetry(() => import("./pages/Portfolio"));
const PublicPortfolio = lazyRetry(() => import("./pages/PublicPortfolio"));
const UserProfilePage = lazyRetry(() => import("./pages/UserProfilePage"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const CompleteProfile = lazyRetry(() => import("./pages/CompleteProfile"));
const TeachingResources = lazyRetry(() => import("./pages/TeachingResources"));
const ReportIssue = lazyRetry(() => import("./pages/ReportIssue"));
const RefundPolicy = lazyRetry(() => import("./pages/RefundPolicy"));
const General = lazyRetry(() => import("./pages/General"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const PresenceTracker = () => {
  usePresence();
  return null;
};

const MfaGate = ({ children }: { children: React.ReactNode }) => {
  const { needsMfaVerify, refreshMfaStatus } = useAuth();
  if (needsMfaVerify) {
    return <MfaVerify onVerified={refreshMfaStatus} />;
  }
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <PresenceTracker />
          <MfaGate>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/apply" element={<TutorApplication />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/bookings" element={<Bookings />} />
                <Route path="/earnings" element={<Earnings />} />
                <Route path="/wallet" element={<Earnings />} />
                <Route path="/withdraw" element={<Earnings />} />
                <Route path="/tutor/:id" element={<TutorProfilePage />} />
                <Route path="/user/:id" element={<UserProfilePage />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/classroom/:bookingId" element={<Classroom />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/portfolio/:tutorId" element={<PublicPortfolio />} />
                <Route path="/complete-profile" element={<CompleteProfile />} />
                <Route path="/resources/:bookingId" element={<TeachingResources />} />
                <Route path="/report-issue/:bookingId" element={<ReportIssue />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/general" element={<General />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <MobileBottomNav />
            <CanonicalTag />
            <HolidayModeBanner />
            <CookieConsent />
            <AffiliateCookieCatcher />
          </MfaGate>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
